import { ImportanceBadge } from '@/components/ui/importance-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { Sparkles, BookOpen } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { DiscoveryCard } from './discovery-card';
import termsIllustration from '@/assets/terms-illustration.png';
import { cn } from '@/lib/utils';

interface CulturalTermCardProps {
    term: {
        term: string;
        originalLanguage: string;
        transliteration?: string;
        gloss: string;
        description: string;
        relevance: string;
        importance?: Importance;
        verseRef?: string;
        sources?: Source[];
    };
    bookName?: string;
    isExpanded: boolean;
    onToggleExpansion: () => void;
}

export function CulturalTermCard({
    term,
    bookName,
    isExpanded,
    onToggleExpansion,
}: CulturalTermCardProps) {
    const isDarkMode = useDarkMode();

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            icon={BookOpen}
            header={
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 relative z-10">
                    <div className="flex flex-col gap-2 flex-1 min-w-0">
                        {term.verseRef && bookName && (
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/40">
                                {bookName} {term.verseRef}
                            </div>
                        )}

                        <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight">
                            {term.term}
                        </h4>

                        {term.gloss && (
                            <p className="text-xs text-muted-foreground italic">
                                {term.gloss}
                            </p>
                        )}

                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted/50 text-muted-foreground">
                                {term.originalLanguage}
                            </span>
                            {term.transliteration && (
                                <span className="text-xs italic text-muted-foreground">
                                    ({term.transliteration})
                                </span>
                            )}
                        </div>

                        {!isExpanded && (
                            <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-primary/40 group-hover/card:text-primary/70 transition-colors mt-2">
                                <Sparkles className="h-2.5 w-2.5" />
                                <span>Discover Meaning</span>
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-5 mt-6 relative z-10">
                {/* Description */}
                {term.description && (
                    <div className="text-sm leading-relaxed text-foreground/80 font-light">
                        {term.description}
                    </div>
                )}

                {/* Relevance Section - Branded Highlight */}
                {term.relevance && (
                    <div className="p-4 rounded-lg bg-primary/[0.03] border border-primary/10 space-y-2 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 flex items-center gap-1.5">
                            <Sparkles className="h-3 w-3" />
                            Biblical Significance
                        </div>
                        <p className="text-sm leading-relaxed text-foreground/80 italic font-serif">
                            {term.relevance}
                        </p>
                    </div>
                )}

                {/* Footer Metadata */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-border/10">
                    <div className="flex items-center gap-3 flex-wrap">
                        {term.importance && (
                            <ImportanceBadge importance={term.importance} context="cultural" />
                        )}
                    </div>

                    {term.sources?.length > 0 && (
                        <SourceLinks sources={term.sources} />
                    )}
                </div>
            </div>
        </DiscoveryCard>
    );
}
